import { DisplayPrice } from 'components/Cart/PriceValue';
import Flex from 'components/ui/Flex';
import Text from 'components/ui/Text';
import useAppDispatch from 'hooks/useAppDispatch';
import useAppSelector from 'hooks/useAppSelector';
import { PriceInformation, PriceValue } from 'microshop-api';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Spinner } from 'reactstrap';
import {
    customerPrice,
    PriceType,
    selectCustomerPrice,
    selectCustomerPricePending,
    selectPriceValue,
} from 'store/reducers/priceSlice';
import styled, { ThemeColors } from 'styled-components/macro';

type ProductPriceProps = {
    price?: PriceValue;
    fallbackPrice?: PriceInformation;
    fontSize?: number;
    color?: keyof ThemeColors;
    compact?: boolean;
    showPriceType?: PriceType;
    usePoints: boolean;
};
const ProductPrice: React.FC<ProductPriceProps> = ({
    price,
    fontSize,
    compact,
    color,
    usePoints,
    fallbackPrice,
}) => {
    const { priceType } = useAppSelector(({ price }) => price);
    const shouldLoadCustomer = useAppSelector(({ shop }) => shop.shop?.isB2BShop);

    const ref = useRef<HTMLDivElement>(null);
    const dispatch = useAppDispatch();

    useEffect(() => {
        let observer: IntersectionObserver | null = null;
        if (ref.current && shouldLoadCustomer && fallbackPrice) {
            const observer = new IntersectionObserver((entries) =>
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        dispatch(customerPrice(fallbackPrice));
                        observer.disconnect();
                    }
                }),
            );
            observer.observe(ref.current);
        }

        return () => {
            if (observer) observer.disconnect();
        };
    }, [ref, dispatch, fallbackPrice, shouldLoadCustomer]);

    if (priceType === ('none' as PriceType)) return null;

    return (
        <div ref={ref} itemProp="offers" itemType="https://schema.org/Offer" itemScope>
            {compact ? (
                <ProductPriceCompact
                    color={color}
                    fontSize={fontSize}
                    price={price}
                    showPriceType={priceType}
                    usePoints={usePoints}
                    fallbackPrice={fallbackPrice}
                />
            ) : (
                <ProductPriceExpanded
                    color={color}
                    fontSize={fontSize}
                    price={price}
                    usePoints={usePoints}
                    fallbackPrice={fallbackPrice}
                />
            )}
        </div>
    );
};

const ProductPriceExpanded: React.FC<ProductPriceProps> = ({
    fontSize = 32,
    color,
    price,
    usePoints,
    fallbackPrice: fallbackPrice,
}) => {
    return (
        <Flex className="mt-1" align="end">
            {price ? (
                <MainPrice className="me-3">
                    <Text fontSize={fontSize} color={color ? color : 'price'}>
                        <PriceString price={price} usePoints={usePoints} />
                    </Text>
                </MainPrice>
            ) : (
                <MainPrice className="me-3">
                    {fallbackPrice ? (
                        <>
                            <Text className="f1-500" small light>
                                <CustomerPricePrefix priceInfo={fallbackPrice!} usePoints={usePoints} />
                            </Text>
                            <Text fontSize={fontSize} color={color ? color : 'price'}>
                                <CustomerPrice priceInfo={fallbackPrice!} usePoints={usePoints} />
                            </Text>
                        </>
                    ) : (
                        <NoPrice />
                    )}
                </MainPrice>
            )}
        </Flex>
    );
};

const ProductPriceCompact: React.FC<ProductPriceProps> = ({
    fontSize = 12,
    color,
    price,
    fallbackPrice,
    usePoints,
}) => {
    return (
        <Text className="d-inline-block" fontSize={fontSize} color={color ? color : 'price'}>
            {price ? (
                <PriceString price={price} usePoints={usePoints} />
            ) : fallbackPrice ? (
                <CustomerPriceString priceInfo={fallbackPrice} usePoints={usePoints} />
            ) : (
                <NoPrice />
            )}
        </Text>
    );
};

type PriceProps = {
    price: PriceValue;
    usePoints: boolean;
};
const PriceString: React.FC<PriceProps> = ({ price, usePoints }) => {
    if (!price) return <NoPrice />;

    return (
        <>
            <StyledPrice>
                {!!price?.str ? <DisplayPrice price={price.str} usePoints={usePoints} /> : <NoPrice />}
                <meta itemProp="price" content={`${price?.str}`} />
            </StyledPrice>
        </>
    );
};
type PriceInfoProps = {
    priceInfo: PriceInformation;
    usePoints: boolean;
};
const CustomerPriceString: React.FC<PriceInfoProps> = ({ priceInfo, usePoints }) => {
    const customerPrice = useAppSelector((state) => selectCustomerPrice(state, priceInfo));
    const pending = useAppSelector((state) => selectCustomerPricePending(state, priceInfo));
    if (!priceInfo) return <NoPrice />;

    if (pending) return <Spinner color="buy" />;
    if (!customerPrice) return <NoPrice />;

    return (
        <>
            <CustomerPricePrefix priceInfo={priceInfo} usePoints={usePoints} />
            <CustomerPrice priceInfo={priceInfo} usePoints={usePoints} />
        </>
    );
};

const CustomerPrice: React.FC<PriceInfoProps> = ({ priceInfo }) => {
    const isSale = undefined;
    const customerPrice = useAppSelector((state) => selectCustomerPrice(state, priceInfo));
    const pending = useAppSelector((state) => selectCustomerPricePending(state, priceInfo));
    const priceValue = useAppSelector((state) => selectPriceValue(state, priceInfo));
    // const isSale = useAppSelector((state) => selectIsSale(state, price));

    if (pending) return <Spinner color="buy" />;
    if (!customerPrice) return <NoPrice />;

    return (
        <>
            <StyledPrice color={isSale ? 'sale' : undefined}>
                {customerPrice?.str ?? <NoPrice />}
                <meta itemProp="price" content={`${customerPrice?.str}`} />
                <meta itemProp="priceCurrency" content={`${priceInfo.currency}`} />
            </StyledPrice>
            {isSale && priceValue.regular?.str && (
                <StyledStrikePrice className="f1-500" color="textLight">
                    {priceValue.regular.str}
                </StyledStrikePrice>
            )}
        </>
    );
};

const CustomerPricePrefix: React.FC<PriceInfoProps> = ({ priceInfo }) => {
    const { t } = useTranslation();
    const isBox = false;
    const isSale = false;
    const format = {
        prefixBox: '',
        prefixSales: '',
        prefix: '',
    };

    // const isBox = useAppSelector((state) => selectIsBox(state, price));
    // const isSale = useAppSelector((state) => selectIsSale(state, price));
    // const format = useAppSelector(({ price }) => price.priceFormat);

    const boxPrefix = format?.prefixBox ?? '';
    const salePrefix = format?.prefixSales ?? '';
    const prefix = format?.prefix ?? '';

    let p: string = prefix;

    if (isSale) p = salePrefix;
    if (isBox) p = boxPrefix;

    return <PriceSpan className="me-1">{p}</PriceSpan>;
};

// const RetailPriceString: React.FC<PriceInfoProps> = ({ price }) => {
//     if (typeof price.retail?.num !== 'number') return null;
//     if (price.retail.num === 0) return <span>-</span>;

//     return (
//         <>
//             <RetailPricePrefix price={price} />
//             <RetailPrice price={price} />
//         </>
//     );
// };

// const RetailPrice: React.FC<PriceInfoProps> = ({ price }) => {
//     return (
//         <StyledPrice>
//             {price.retail?.str}
//             <meta itemProp="price" content={`${price.retail?.str}`} />
//             <meta itemProp="priceCurrency" content={`${price.currency}`} />
//         </StyledPrice>
//     );
// };

// const RetailPricePrefix: React.FC<PriceInfoProps> = ({ price }) => {
//     const { t } = useTranslation();
//     const isBox = false;

//     const format = useAppSelector(({ price }) => price.priceFormat);
//     const isBox = useAppSelector((state) => selectIsBox(state, price));

//     return (
//         <PriceSpan className="me-1">
//             {/* {format?.prefixRetail ? format.prefixRetail : t('price.rrp_short', 'RRP')} */}
//             {isBox ? ' / ' + t('stock_pcs', 'pcs') : ''}
//         </PriceSpan>
//     );
// };

const NoPrice = () => {
    return null;
};

export default ProductPrice;

const Price = styled.div``;

const MainPrice = styled(Price)`
    line-height: 1.2;
`;

const PriceSpan = styled.span`
    display: inline-block;
`;

const StyledPrice = styled(PriceSpan)<{ color?: keyof ThemeColors }>`
    ${({ color, theme }) => color && `color: ${theme.colors[color]};`}
`;

const StyledStrikePrice = styled(StyledPrice)`
    text-decoration: line-through;
    margin-left: 0.4em;
`;
